import * as THREE from 'three';
import { ConfiguratorModule } from '../types/index';

/*
 * Furniture configurator module — Black Leather Chair
 *
 * Loads the real GLB from public/models/black_leather_chair.glb.
 * Falls back to a procedural chair placeholder if the load fails.
 *
 * Provides:
 *   - Leather-specific colour palette (Black / Brown / Tan / White)
 *   - Variant presets (Standard / Premium finish)
 *   - Material-safe applyColor: clones GLTF materials before mutation so
 *     originals are never overwritten and disposeObject() can free clones.
 */
const furniture: ConfiguratorModule = {
  type: 'furniture',
  label: 'Furniture',
  modelPath: '/models/black_leather_chair.glb',

  // The GLB contains a 'seat' node (the chair) and a 'Plane' node (baked
  // shadow/floor mesh). Without this, the Plane inflates the bounding box
  // and shrinks the chair to a dot. cameraTarget limits scale + focus to
  // the seat node only.
  cameraTarget: 'seat',

  hotspots: [
    { label: 'Seat',     offset: [ 0,    0.05,  0.55] }, // front of seat cushion
    { label: 'Backrest', offset: [ 0,    0.90, -0.35] }, // upper backrest
  ],

  // GLB default orientation faces +X (right). Rotate -90° around Y so
  // the chair front faces +Z (towards the camera).
  modelRotationY: -Math.PI / 2,

  colorOptions: [
    { label: 'Default', hex: '' },         // GLB natural colours — no override
    { label: 'Black',   hex: '#1a1a1a' },
    { label: 'Brown',   hex: '#6b3a2a' },
    { label: 'Tan',     hex: '#c4a265' },
    { label: 'White',   hex: '#f0ebe3' },
  ],

  variantOptions: [
    { id: 'premium',  label: 'Premium'  },
    { id: 'standard', label: 'Standard' },
  ],

  variantModelPaths: {
    premium:  '/models/black_leather_chair.glb',
    standard: '/models/standard_chair.glb',
  },

  /**
   * Applies a colour to all non-metallic surfaces.
   *
   * hex === '' → restore the GLB's original materials (no colour override).
   *
   * Clone strategy:
   *   - On first colour selection, original materials are saved in
   *     child.userData.furnitureOriginals so they can be restored later.
   *   - A single clone per original is created and cached in
   *     originalMat.userData.furnitureClone to avoid repeated allocation.
   *   - Metallic materials (metalness > 0.5) are skipped so the frame/legs
   *     retain their original finish at all times.
   */
  applyColor(model: THREE.Object3D, hex: string): void {
    model.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return;

      const rawMats: THREE.Material[] = Array.isArray(child.material)
        ? (child.material as THREE.Material[])
        : [child.material as THREE.Material];

      // Snapshot originals on first encounter (before any mutation)
      if (!child.userData.furnitureOriginalsStored) {
        child.userData.furnitureOriginals        = rawMats.slice();
        child.userData.furnitureOriginalsStored  = true;
      }

      const originals = child.userData.furnitureOriginals as THREE.Material[];

      // '' → restore GLB defaults
      if (!hex) {
        child.material = Array.isArray(child.material) ? originals.slice() : originals[0];
        return;
      }

      const targetColor = new THREE.Color(hex);

      const processed = originals.map((orig) => {
        const stdOrig = orig as THREE.MeshStandardMaterial;
        if (stdOrig.metalness !== undefined && stdOrig.metalness > 0.5) return orig;

        // Reuse or create a clone
        if (!orig.userData.furnitureClone) {
          const clone = orig.clone() as THREE.MeshStandardMaterial;
          clone.userData.isFurnitureClone = true;
          orig.userData.furnitureClone    = clone;
        }

        const clone = orig.userData.furnitureClone as THREE.MeshStandardMaterial;
        if ('color' in clone) clone.color.set(targetColor);
        clone.needsUpdate = true;
        return clone;
      });

      child.material = Array.isArray(child.material) ? processed : processed[0];
    });
  },

  /**
   * Adjusts surface roughness / envMap intensity to simulate two finish grades.
   *   Standard → matte leather   (roughness 0.65)
   *   Premium  → patent leather  (roughness 0.18, slightly reflective)
   *
   * Operates on whichever materials are currently on each mesh (including clones),
   * so it correctly combines with a prior applyColor call.
   */
  applyVariant(model: THREE.Object3D, variantId: string): void {
    const isPremium = variantId === 'premium';

    model.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return;

      const mats = Array.isArray(child.material)
        ? (child.material as THREE.Material[])
        : [child.material as THREE.Material];

      mats.forEach((mat) => {
        if (!(mat instanceof THREE.MeshStandardMaterial)) return;
        if (mat.metalness > 0.5) return; // skip frame hardware

        mat.roughness        = isPremium ? 0.18 : 0.65;
        mat.envMapIntensity  = isPremium ? 1.5  : 0.8;
        mat.needsUpdate      = true;
      });
    });
  },

  // ── Procedural fallback ───────────────────────────────────────────────────
  // Used when modelPath fails to load (e.g. file missing in dev).

  buildPlaceholder(): THREE.Object3D {
    const group = new THREE.Group();
    group.name = 'placeholder-furniture';

    const leatherMat = new THREE.MeshStandardMaterial({
      color: 0x1a1a1a,
      roughness: 0.65,
      metalness: 0.0,
    });
    const frameMat = new THREE.MeshStandardMaterial({
      color: 0x2a2a2a,
      roughness: 0.3,
      metalness: 0.75,
    });

    // Seat cushion
    const seat = new THREE.Mesh(new THREE.BoxGeometry(1.4, 0.2, 1.3), leatherMat);
    seat.position.y = 0.9;
    seat.castShadow = true;
    group.add(seat);

    // Back cushion
    const back = new THREE.Mesh(new THREE.BoxGeometry(1.4, 1.2, 0.15), leatherMat);
    back.position.set(0, 1.55, -0.57);
    back.castShadow = true;
    group.add(back);

    // Armrests
    const armGeo = new THREE.BoxGeometry(0.13, 0.09, 1.0);
    [-0.67, 0.67].forEach((x) => {
      const arm = new THREE.Mesh(armGeo, leatherMat);
      arm.position.set(x, 1.08, 0.02);
      arm.castShadow = true;
      group.add(arm);
    });

    // Legs
    const legGeo = new THREE.CylinderGeometry(0.045, 0.045, 0.9, 14);
    const legOffsets: [number, number][] = [
      [-0.55, -0.5], [0.55, -0.5], [-0.55, 0.5], [0.55, 0.5],
    ];
    legOffsets.forEach(([x, z]) => {
      const leg = new THREE.Mesh(legGeo, frameMat);
      leg.position.set(x, 0.45, z);
      leg.castShadow = true;
      group.add(leg);
    });

    return group;
  },
};

export default furniture;
